/**
 * Export methods from a worker to the main thread.
 *
 * @param handlers Object with methods to export
 *
 * @example
 * ```ts
 * // my-worker.ts
 * function foo() { return 'bar'; }
 *
 * async function asyncFoo() { return 'bar'; }
 *
 * export default exportWorker({
 *   foo,
 *   asyncFoo,
 *   inline: () => 'bar',
 * });
 * ```
 */
export function exportWorker(handlers) {
    self.onmessage = async ({ data }) => {
        try {
            // Get handler from registrations
            const handler = handlers[data.name];
            if (!handler)
                throw new Error(`[BUG] No handler for type ${data.name}`);
            // Run handler
            let result = handler.apply(self, data.args);
            if (result instanceof Promise) {
                result = await result;
            }
            // Success - post back to main thread
            self.postMessage({ reqid: data.reqid, resolve: result });
        }
        catch (e) {
            // Error - post back rejection
            self.postMessage({ reqid: data.reqid, reject: e.message });
        }
    };
    return null;
}
/**
 * Import a worker exported with `exportWorker`.
 *
 * @param worker Worker to import
 *
 * @example
 * ```ts
 * // main.ts
 * import type MyWorker from './my-worker.ts';
 *
 * const worker = importWorker<typeof MyWorker>(new Worker(new URL('./XImgWorkerStub.ts', import.meta.url)));
 *
 * (async () => {
 *   // all methods are async
 *   console.assert(await worker.foo() === 'bar');
 *   console.assert(await worker.asyncFoo() === 'bar');
 *   console.assert(await worker.inline() === 'bar');
 * })();
 * ```
 */
export function importWorker(worker) {
    const promises = new Map();
    // Handle messages from worker
    worker.onmessage = ({ data }) => {
        var _a, _b;
        const { reqid, resolve, reject } = data;
        if (resolve)
            (_a = promises.get(reqid)) === null || _a === void 0 ? void 0 : _a.resolve(resolve);
        if (reject)
            (_b = promises.get(reqid)) === null || _b === void 0 ? void 0 : _b.reject(reject);
        promises.delete(reqid);
    };
    // Create proxy to call worker methods
    const proxy = new Proxy(worker, {
        get(target, name) {
            return async function wrapper(...args) {
                return await new Promise((resolve, reject) => {
                    const reqid = Math.random();
                    promises.set(reqid, { resolve, reject });
                    target.postMessage({ reqid, name, args }, {
                        transfer: args.filter(isTransferred)
                    });
                });
            };
        },
    });
    return proxy;
}
// Key to mark an object as transferred
const TRANSFERRED_KEY = "_wwt_is_transferred_";
/**
 * Mark an object as transferred.
 *
 * @param object Object to transfer
 *
 * @example
 * ```ts
 * // main.ts
 * worker.foo(transfer(new ArrayBuffer(1000)));
 */
export function transfer(object) {
    if (typeof object === 'object') {
        object[TRANSFERRED_KEY] = true;
        return object;
    }
    throw new Error('Only objects can be transferred');
}
/**
 * Check if an object is transferred.
 */
function isTransferred(object) {
    return typeof object === 'object' && object[TRANSFERRED_KEY] === true;
}
